/**
 * i18n — Đa ngôn ngữ giao diện (i18next + react-i18next).
 *
 * - Tiếng Việt là ngôn ngữ nguồn + dự phòng: `src/locales/vi/<namespace>.json`.
 * - Mỗi ngôn ngữ khác là file tĩnh do `scripts/i18n-translate.mjs` máy dịch.
 * - MỌI bản dịch (kể cả tiếng Việt) được nạp lười bằng dynamic import — mỗi
 *   `<lang>/<ns>.json` là một chunk riêng, bundle chính không phình theo số ngôn ngữ.
 * - Khi khởi động / đổi ngôn ngữ, nạp TẤT CẢ namespace của ngôn ngữ đó trước rồi
 *   mới hiển thị, nên `i18n.t()` gọi ngoài component (toast trong hook, hằng số…)
 *   cũng luôn có chữ.
 *
 * Cách dùng trong component:
 *   const { t } = useTranslation('conversations')
 *   t('list.empty')                 // khoá trong namespace mặc định của hook
 *   t('common:actions.save')        // khoá namespace khác
 *   t('list.count', { count: 5 })   // nội suy {{count}}
 * Ngoài component: `import { i18n } from '@/lib/i18n'` rồi `i18n.t('ns:key')`.
 */
import i18next, { type BackendModule, type ReadCallback } from 'i18next'
import { initReactI18next } from 'react-i18next'
import { LANGUAGE_CODES, SOURCE_LANGUAGE, findLanguage, isRtl, matchLanguage } from '@/locales/languages'

export const i18n = i18next

const loaders = import.meta.glob<{ default: Record<string, unknown> }>('../../locales/*/*.json')

const pathOf = (lng: string, ns: string) => `../../locales/${lng}/${ns}.json`

/** Namespace = tên file trong `locales/vi/` (biết lúc build, không cần tải file). */
export const NAMESPACES = Object.keys(loaders)
  .map((p) => /\/locales\/vi\/([^/]+)\.json$/.exec(p)?.[1])
  .filter((x): x is string => !!x)
  .sort()

const lazyBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(lng: string, ns: string, cb: ReadCallback) {
    const load = loaders[pathOf(lng, ns)]
    // Ngôn ngữ chưa có file cho namespace này → rỗng, i18next tự rơi về tiếng Việt.
    if (!load) return cb(null, {})
    load().then(
      (m) => cb(null, m.default),
      (err: unknown) => cb(err instanceof Error ? err : new Error(String(err)), false),
    )
  },
}

export const LANG_STORAGE_KEY = 'ecdp_lang'

/** Lựa chọn người dùng đã lưu trên trình duyệt này (nếu có). */
export function storedLanguage(): string | undefined {
  try {
    const v = localStorage.getItem(LANG_STORAGE_KEY)
    return v && findLanguage(v) ? v : undefined
  } catch {
    return undefined
  }
}

export function rememberLanguage(code: string) {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code)
  } catch {
    /* trình duyệt chặn lưu — vẫn áp dụng cho phiên này */
  }
}

/** Ngôn ngữ trình duyệt nếu được hỗ trợ, không thì tiếng Việt. */
export function browserLanguage(): string {
  const list = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : []
  for (const raw of list) {
    const m = matchLanguage(raw)
    if (m) return m
  }
  return SOURCE_LANGUAGE
}

export function initialLanguage(): string {
  return storedLanguage() ?? browserLanguage()
}

/** Đặt `<html lang dir>` theo ngôn ngữ đang dùng. */
export function applyDocumentLanguage(code: string) {
  if (typeof document === 'undefined') return
  const el = document.documentElement
  el.lang = code
  el.dir = isRtl(code) ? 'rtl' : 'ltr'
}

/** Mã locale cho Intl (định dạng số/ngày) theo ngôn ngữ đang dùng. */
export function currentLocale(): string {
  return i18next.resolvedLanguage || i18next.language || SOURCE_LANGUAGE
}

let ready: Promise<unknown> | null = null

/** Khởi tạo một lần; trả promise xong khi mọi namespace của ngôn ngữ đầu đã nạp. */
export function initI18n(lng = initialLanguage()): Promise<unknown> {
  if (ready) return ready
  i18next.on('languageChanged', applyDocumentLanguage)
  ready = i18next
    .use(lazyBackend)
    .use(initReactI18next)
    .init({
      lng,
      fallbackLng: SOURCE_LANGUAGE,
      supportedLngs: LANGUAGE_CODES,
      nonExplicitSupportedLngs: false,
      load: 'currentOnly',
      ns: NAMESPACES,
      defaultNS: 'common',
      fallbackNS: 'common',
      interpolation: { escapeValue: false },
      returnNull: false,
      react: { useSuspense: false, bindI18n: 'languageChanged loaded' },
    })
    .then(() => applyDocumentLanguage(i18next.language))
  return ready
}

/** Đổi ngôn ngữ ngay (không tải lại trang); nạp đủ namespace rồi mới chuyển. */
export async function changeLanguage(code: string, remember = true) {
  if (!findLanguage(code)) return
  if (remember) rememberLanguage(code)
  if (i18next.language === code) return
  await i18next.changeLanguage(code)
}
